import { Typography } from "@mui/material";
import moment from "moment";
import { forwardRef } from "react";
import CustomButton from "../../../components/CustomButton";
import ContentToPrint from "../ContentToPrint/ContentToPrint";

const SOA_INVOICE_KEYS = [
  "invoiceNumbers",
  "petShopInvoiceNumber",
  "treatmentInvoiceNumber",
];

export const buildSoaList = (billingData = []) => {
  const allPaymentIds = (billingData ?? [])?.map((li) => li?.paymentId);
  const reqList = (billingData ?? [])
    ?.filter((li) => {
      const isApnt = li?.appointmentId;
      const isPos = li?.type === "Pharmacy" || li?.type === "Pet Shop";
      const isLength = (li?.paymentDetails ?? [])?.length > 0;
      return (isApnt || isPos) && isLength;
    })
    ?.flatMap((item) => {
      const matched = SOA_INVOICE_KEYS.map((key) => ({
        key,
        value: item?.[key],
      }))?.find((inv) => inv?.value?.length > 0);
      const comTotal =
        (item?.totalAmt ?? 0) +
        (item?.pharmacyTotalAmt ?? 0) +
        (item?.petShopTotalAmt ?? 0);
      const main = {
        type: "INVOICE",
        itemDate: item?.createdDate,
        paymentId: item?.paymentId,
        itemType: matched?.value?.includes("ESTP")
          ? "Proforma Invoice"
          : "Invoice",
        refNo: matched?.value ?? "",
        source: "",
        paymentMode: "",
        debit: comTotal,
        credit: 0,
        petName: item?.petName ?? "",
      };
      const collections = (item.paymentCollections || [])
        ?.filter((li) => allPaymentIds?.includes(li?.paymentId))
        ?.map((p) => ({
          type: "PAYMENT",
          itemDate: p?.receiptDate,
          collectionId: p?.collectionId,
          refNo: p?.receiptNo ?? p?.invoiceNo ?? "",
          source: p?.source,
          paymentMode: p?.paymentMode ?? "",
          debit: 0,
          credit: p?.amountPaid,
          petName: item?.petName ?? "",
        }));
      return [main, ...collections];
    });

  const sortedList = reqList?.sort(
    (a, b) => new Date(a?.itemDate) - new Date(b?.itemDate),
  );
  let runningBalance = 0;
  return sortedList?.map((li) => {
    runningBalance += (li?.credit || 0) - (li?.debit || 0);
    return { ...li, balanceDue: runningBalance };
  });
};

export const buildCustomerSoaList = (customerData = []) => {
  const list = Array.isArray(customerData)
    ? customerData
    : (customerData?.data ?? []);
  const reqList = (list ?? [])?.map((item) => ({
    type: "INVOICE",
    itemDate: item?.createdDate,
    paymentId: item?.paymentId,
    itemType: (item?.invoiceNumber ?? "")?.includes("ESTP")
      ? "Proforma Invoice"
      : "Invoice",
    refNo: item?.invoiceNumber ?? "",
    source: item?.type ?? "",
    paymentMode: item?.paymentMode ?? "",
    debit: Number(item?.totalAmt ?? 0),
    credit: Number(item?.amtPaid ?? 0),
    petName: item?.petName ?? "",
    userName: item?.userName ?? "",
  }));

  const sortedList = reqList?.sort(
    (a, b) => new Date(a?.itemDate) - new Date(b?.itemDate),
  );
  let runningBalance = 0;
  return sortedList?.map((li) => {
    runningBalance += (li?.credit || 0) - (li?.debit || 0);
    return { ...li, balanceDue: runningBalance };
  });
};

export const getSoaTotals = (soaList = []) =>
  (soaList ?? []).reduce(
    (acc, item) => {
      acc.totalDebit += item?.debit || 0;
      acc.totalCredit += item?.credit || 0;
      return acc;
    },
    { totalDebit: 0, totalCredit: 0 },
  );

const StatementOfAccounts = forwardRef(
  (
    {
      clinicDet,
      petName,
      userName,
      startDate,
      endDate,
      soaList = [],
      onPrint,
      showEmptyMessage = true,
      showPetNameColumn = false,
    },
    ref,
  ) => {
    const totals = getSoaTotals(soaList);
    const balanceTotal =
      Number(totals?.totalCredit ?? 0) - Number(totals?.totalDebit ?? 0);

    return (
      <>
        <ContentToPrint
          ref={ref}
          header={
            <div className="flex-row-ali-cen mv20">
              <div className="w35Per">
                <div>
                  {clinicDet?.image ? (
                    <img src={clinicDet?.image} className="img-h75" alt="" />
                  ) : (
                    <div className="flex-center h50img">
                      <Typography className="font-bold fs30 white-color capitalize">
                        {clinicDet?.name?.[0]}
                      </Typography>
                    </div>
                  )}
                </div>
              </div>
              <div className="w30Per flex-center">
                <div className="font-bold fs20 blue-color">
                  Statement Of Accounts
                </div>
              </div>
              <div className="w35Per flex1-end">
                <div className="prescription-doc-header-clinic">
                  <div className="font-bold blue-color fs16">
                    {clinicDet?.name ?? ""}
                  </div>
                  <div className="txt-regular fs12 blue-color">
                    {clinicDet?.address ?? ""}
                  </div>
                  {clinicDet?.taxId?.length > 0 ? (
                    <div className="flex-row-ali-cen mt5">
                      <div className="font-bold fs12 blue-color">TAX ID: </div>
                      <div className="txt-regular fs12 blue-color ml3">
                        {clinicDet?.taxId ?? ""}
                      </div>
                    </div>
                  ) : null}
                  {clinicDet?.mobile?.length > 0 ? (
                    <div className="flex-row-ali-cen mt5">
                      <div className="font-bold fs12 blue-color">Contact: </div>
                      <div className="txt-regular fs12 blue-color ml3">
                        {clinicDet?.mobile ?? ""}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          }
          footer={
            <div className="prescription-print-footer-inner mt20">
              {clinicDet?.footer?.length > 0 ? (
                <img src={clinicDet?.footer} alt="" />
              ) : null}
            </div>
          }
        >
          <tr>
            <td className="prescription-print-tbody-cell">
              <div className="prescription-print-body-inner">
                <div>
                  <div className="flex-row-ali-cen">
                    <div className="flex-row-ali-cen">
                      <Typography className="font-bold fs14 gray4">
                        {showPetNameColumn
                          ? "Parent Name"
                          : "Pet / Parent Name"}
                      </Typography>
                      <div className="txt-semi-bold fs14 black capitalize ml10">
                        {showPetNameColumn
                          ? (userName ?? "")
                          : `${petName ?? ""} / ${userName ?? ""}`}
                      </div>
                    </div>
                    <div className="flex1-end">
                      <div className="flex-row-ali-cen">
                        <Typography className="font-bold fs14 gray4">
                          Date Range
                        </Typography>
                        <div className="txt-semi-bold fs14 black capitalize ml10">
                          {moment(new Date(startDate)).format("DD MMM YYYY")} -{" "}
                          {moment(new Date(endDate)).format("DD MMM YYYY")}
                        </div>
                      </div>
                    </div>
                  </div>
                  {soaList?.length > 0 ? (
                    <>
                      <div className="thin-line mt10" />
                      <table className="custom-table">
                        <thead>
                          <tr>
                            {showPetNameColumn ? (
                              <th className="w12Per">Pet Name</th>
                            ) : null}
                            <th className="w10Per">Tr. Date</th>
                            <th className="w10Per">Type</th>
                            <th className="w15Per">Ref. No.</th>
                            <th className="w10Per">Source</th>
                            <th className="w13Per">Payment Mode</th>
                            <th className="w12Per">Debit</th>
                            <th className="w12Per">Credit</th>
                            <th className="w12Per">Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {soaList?.map((bl, i) => (
                            <tr
                              key={`${bl?.paymentId ?? bl?.collectionId}_${i}_soa`}
                            >
                              {showPetNameColumn ? (
                                <td>{bl?.petName ?? ""}</td>
                              ) : null}
                              <td>
                                {moment(new Date(bl?.itemDate)).format(
                                  "DD MMM YYYY",
                                )}
                              </td>
                              <td>{bl?.itemType ?? ""}</td>
                              <td>{bl?.refNo ?? ""}</td>
                              <td>{bl?.source ?? ""}</td>
                              <td>{bl?.paymentMode ?? ""}</td>
                              <td>{Number(bl?.debit)?.toFixed(2)}</td>
                              <td>{Number(bl?.credit)?.toFixed(2)}</td>
                              <td>{Number(bl?.balanceDue)?.toFixed(2)}</td>
                            </tr>
                          ))}
                          <tr>
                            <td
                              colSpan={showPetNameColumn ? 9 : 8}
                              style={{ padding: 0 }}
                            >
                              <div className="thin-line" />
                            </td>
                          </tr>
                          <tr key="tot">
                            {showPetNameColumn ? <td></td> : null}
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td>
                              <div className="font-bold">Total</div>
                            </td>
                            <td>
                              <div className="font-bold">
                                {Number(totals?.totalDebit ?? 0)?.toFixed(2)}
                              </div>
                            </td>
                            <td>
                              <div className="font-bold">
                                {Number(totals?.totalCredit ?? 0)?.toFixed(2)}
                              </div>
                            </td>
                            <td>
                              <div className="font-bold">
                                {Number(balanceTotal)?.toFixed(2)}
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      <div className="thin-line" />
                    </>
                  ) : showEmptyMessage ? (
                    <Typography className="txt-regular fs14 flex-center mt20">
                      No records available
                    </Typography>
                  ) : null}
                </div>
              </div>
            </td>
          </tr>
        </ContentToPrint>
        <div className="flex-end mv20">
          <div>
            <CustomButton text="Print" smallBtn onClick={onPrint} />
          </div>
        </div>
      </>
    );
  },
);

StatementOfAccounts.displayName = "StatementOfAccounts";

export default StatementOfAccounts;
